import { Stack, Typography } from "@altimateai/lego";

// Tab identifiers must stay in sync with DISCOVER_TABS in
// DiscoverContentV2.tsx — the empty state per-tab copy depends on
// which tab the user is looking at.
export type DiscoverEmptyStateTab = "opportunities" | "implemented" | "groups";

const COPY: Record<
  DiscoverEmptyStateTab,
  { title: string; description: string }
> = {
  opportunities: {
    title: "No opportunities yet",
    description:
      "There are no active or dismissed recommendations in the current date range. Adjust the date range or filters above to broaden the search.",
  },
  implemented: {
    title: "No implemented opportunities yet",
    description:
      "Opportunities you mark as Implemented from the Opportunities tab will appear here, so you can track the impact of changes over time.",
  },
  groups: {
    title: "No opportunity groups",
    description:
      "Groups bundle similar recommendations across resources. Adjust filters or date range to broaden the search.",
  },
};

interface DiscoverEmptyStateProps {
  tab: DiscoverEmptyStateTab;
}

// Visual shape borrowed from `SavingsEmptyState` in
// `pages/databricks/summary/components/` so the empty-table message on
// /discover matches the rest of the product — a slate dashed-border
// card with a centered icon, title, and supporting copy.
export const DiscoverEmptyState = ({ tab }: DiscoverEmptyStateProps) => {
  const { title, description } = COPY[tab];
  return (
    <Stack
      direction="column"
      align="center"
      justify="center"
      role="status"
      aria-live="polite"
      data-testid={`discover-empty-state-${tab}`}
      className="al-w-full al-min-h-[280px] al-px-8 al-py-12 al-bg-slate-50 al-rounded-xl al-border al-border-dashed al-border-slate-200"
    >
      <Stack
        align="center"
        justify="center"
        className="al-w-16 al-h-16 al-mb-4 al-rounded-full al-bg-teal-50"
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#01CD8C"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 3v18h18" />
          <path d="M7 14l4-4 4 4 5-5" />
        </svg>
      </Stack>
      {/* Typography hierarchy:
        *
        *   h3        → 20px semibold → page-empty-state title
        *   body-reg  → 16px regular  → supporting copy
        *
        * Sizes come from `apps/web/tailwind.config.ts` (h3 = 1.25rem,
        * body-regular = 1rem). `h4` would render at 16px — same as
        * body-regular — so the title would visually equal the body
        * text and lose its "this is the heading" signal. */}
      <Typography
        variant="h3"
        weight="semibold"
        className="al-mb-2 al-text-slate-800"
      >
        {title}
      </Typography>
      <Typography
        variant="body"
        className="al-max-w-[480px] al-text-center al-text-slate-500 al-leading-relaxed"
      >
        {description}
      </Typography>
    </Stack>
  );
};

export default DiscoverEmptyState;
